import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, Link } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import {
    FiDollarSign,
    FiTrendingUp,
    FiDownload,
    FiEye,
    FiCreditCard,
    FiBarChart2,
    FiAlertCircle,
    FiRefreshCw,
    FiFilter
} from 'react-icons/fi'
import { formatCurrency, formatDate } from '@/utils/formatters'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { UserBalanceCard } from '@/components/subscription/UserBalanceCard'
import { CommissionBreakdown } from '@/components/subscription/CommissionBreakdown'
import { useSnackbar } from '@/hooks/useSnackbar'

interface EarningsStats {
    totalEarnings: number
    currentMonthEarnings: number
    pendingPayments: number
    availableBalance: number
    averageEarningsPerGig: number
    totalCompletedGigs: number
    earningsGrowth: number
    lastPayoutDate: string
}

interface Transaction {
    id: string
    gigTitle: string
    amount: number
    platformCommission: number
    buyerProtectionFee: number
    netAmount: number
    date: string
    status: 'completed' | 'pending' | 'failed'
    type: 'payment_received' | 'withdrawal' | 'fee'
}

const EarningsPage: React.FC = () => {
    const { user } = useAppSelector((state) => state.auth)
    const { showSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const [earningsData, setEarningsData] = useState<EarningsStats | null>(null)
    const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([])
    const [loading, setLoading] = useState(false)
    const [withdrawalLoading, setWithdrawalLoading] = useState(false)
    const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([])
    const [transactionFilter, setTransactionFilter] = useState<'all' | 'payment' | 'withdrawal' | 'fee'>('all')

    // Initialize earnings data from user object and load transactions
    useEffect(() => {
        if (user && user.earnings) {
            const stats: EarningsStats = {
                totalEarnings: user.earnings.totalEarned || 0,
                currentMonthEarnings: 0, // Will be calculated from transactions if available
                pendingPayments: user.earnings.pendingPayouts || 0,
                availableBalance: user.earnings.availableBalance || 0,
                averageEarningsPerGig: 0, // Will be calculated from actual data
                totalCompletedGigs: 0, // From activity metrics
                earningsGrowth: 0, // Will track from history
                lastPayoutDate: new Date().toISOString()
            }

            // Calculate from activity metrics if available
            if (user.activityMetrics && user.activityMetrics.successfulCompletions) {
                stats.totalCompletedGigs = user.activityMetrics.successfulCompletions
                if (stats.totalCompletedGigs > 0) {
                    stats.averageEarningsPerGig = stats.totalEarnings / stats.totalCompletedGigs
                }
            }

            setEarningsData(stats)
            
            // Load transactions from API
            loadTransactionsFromAPI()
        }
    }, [user])

    const loadTransactionsFromAPI = async () => {
        try {
            setLoading(true)
            const token = localStorage.getItem('token')
            
            const response = await fetch('/api/v1/subscription/transactions?limit=50', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch transactions')
            }

            const data = await response.json()
            
            if (data.success && data.data && Array.isArray(data.data)) {
                // Transform API response to Transaction format
                const transactions: Transaction[] = data.data.map((t: any) => ({
                    id: t._id,
                    gigTitle: t.reference || t.description || 'Payment',
                    amount: t.amount || 0,
                    platformCommission: t.platformCommission || 0,
                    buyerProtectionFee: t.buyerProtectionFee || 0,
                    netAmount: (t.amount || 0) - (t.platformCommission || 0) - (t.buyerProtectionFee || 0),
                    date: t.initiatedAt || t.createdAt || new Date().toISOString(),
                    status: t.status || 'pending',
                    type: t.type || 'payment_received'
                }))

                setRecentTransactions(transactions)
                setFilteredTransactions(transactions)
            } else {
                // If API call fails, show empty state
                setRecentTransactions([])
                setFilteredTransactions([])
            }
        } catch (error) {
            console.error('Error loading transactions:', error)
            showSnackbar('Failed to load transactions', { type: 'error' })
            setRecentTransactions([])
            setFilteredTransactions([])
        } finally {
            setLoading(false)
        }
    }

    const handleFilterChange = (filter: 'all' | 'payment' | 'withdrawal' | 'fee') => {
        setTransactionFilter(filter)

        if (filter === 'all') {
            setFilteredTransactions(recentTransactions)
        } else {
            const typeMap: Record<string, string> = {
                payment: 'payment_received',
                withdrawal: 'withdrawal',
                fee: 'fee'
            }

            const filtered = recentTransactions.filter(t => t.type === typeMap[filter])
            setFilteredTransactions(filtered)
        }
    }

    const handleWithdrawal = async () => {
        try {
            setWithdrawalLoading(true)

            // Initiate withdrawal request
            const response = await fetch('/api/v1/earnings/withdraw', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    amount: earningsData?.availableBalance
                })
            })

            if (!response.ok) throw new Error('Withdrawal request failed')

            const data = await response.json()

            if (data.success) {
                showSnackbar('Withdrawal request submitted successfully', { type: 'success' })
                // Reload earnings data
                window.location.reload()
            } else {
                showSnackbar(data.message || 'Withdrawal failed', { type: 'error' })
            }
        } catch (error) {
            console.error('Withdrawal error:', error)
            showSnackbar('Failed to process withdrawal', { type: 'error' })
        } finally {
            setWithdrawalLoading(false)
        }
    }

    const handleRefresh = async () => {
        setLoading(true)
        try {
            // Reload earnings data
            window.location.reload()
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    if (!earningsData) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center">
                    <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Failed to load earnings
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Unable to retrieve your earnings data. Please try again.
                    </p>
                    <button
                        onClick={handleRefresh}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
                    >
                        <FiRefreshCw className="w-4 h-4" />
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    return (
        <>
            <Helmet>
                <title>Earnings - SideGO</title>
                <meta name="description" content="Track your earnings and payment history" />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Earnings Overview
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Track your income, payments, and financial performance
                        </p>
                    </div>
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors disabled:opacity-50"
                    >
                        <FiRefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* User Balance Card */}
                {user && (
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Balance Overview
                            </h2>
                            <Link
                                to="/dashboard/wallet"
                                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium inline-flex items-center gap-1"
                            >
                                <FiEye className="w-4 h-4" />
                                View Wallet Details
                            </Link>
                        </div>
                        <UserBalanceCard
                            balance={earningsData.availableBalance}
                            pending={earningsData.pendingPayments}
                            earned={earningsData.totalEarnings}
                            currency="GHS"
                            onWithdraw={() => navigate('/dashboard/wallet')}
                            withdrawalDisabled={earningsData.availableBalance <= 0}
                            loading={withdrawalLoading}
                        />
                    </div>
                )}

                {/* Earnings Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Total Earnings
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {formatCurrency(earningsData.totalEarnings, 'GHS')}
                                </p>
                            </div>
                            <div className="bg-green-100 dark:bg-green-900 p-3 rounded-full">
                                <FiDollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    This Month
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {formatCurrency(earningsData.currentMonthEarnings, 'GHS')}
                                </p>
                                <div className="flex items-center gap-1 mt-1">
                                    <FiTrendingUp className="w-4 h-4 text-green-500" />
                                    <span className="text-sm text-green-600 dark:text-green-400">
                                        +{earningsData.earningsGrowth}%
                                    </span>
                                </div>
                            </div>
                            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
                                <FiBarChart2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Pending Payments
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {formatCurrency(earningsData.pendingPayments, 'GHS')}
                                </p>
                            </div>
                            <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-full">
                                <FiCreditCard className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Avg per Gig
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {formatCurrency(earningsData.averageEarningsPerGig, 'GHS')}
                                </p>
                            </div>
                            <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-full">
                                <FiEye className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Transactions */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <FiFilter className="w-5 h-5" />
                            Recent Transactions
                        </h2>
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
                        {(['all', 'payment', 'withdrawal', 'fee'] as const).map((filter) => (
                            <button
                                key={filter}
                                onClick={() => handleFilterChange(filter)}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
                                    transactionFilter === filter
                                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>

                    {/* Transactions List */}
                    {filteredTransactions.length > 0 ? (
                        <div className="space-y-4">
                            {filteredTransactions.map((transaction) => (
                                <div
                                    key={transaction.id}
                                    className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                    <div className="flex items-start gap-3 flex-1">
                                        <div
                                            className={`p-2 rounded-full ${
                                                transaction.status === 'completed'
                                                    ? 'bg-green-100 dark:bg-green-900'
                                                    : transaction.status === 'pending'
                                                        ? 'bg-yellow-100 dark:bg-yellow-900'
                                                        : 'bg-red-100 dark:bg-red-900'
                                            }`}
                                        >
                                            <FiDollarSign
                                                className={`w-4 h-4 ${
                                                    transaction.status === 'completed'
                                                        ? 'text-green-600 dark:text-green-400'
                                                        : transaction.status === 'pending'
                                                            ? 'text-yellow-600 dark:text-yellow-400'
                                                            : 'text-red-600 dark:text-red-400'
                                                }`}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {transaction.gigTitle || transaction.type.replace('_', ' ')}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {formatDate(transaction.date)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="text-right ml-4">
                                        <p className="font-semibold text-gray-900 dark:text-white">
                                            {formatCurrency(transaction.amount, 'GHS')}
                                        </p>
                                        <div className="flex items-center justify-end gap-2 mt-1">
                                            <span
                                                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                                    transaction.status === 'completed'
                                                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                                        : transaction.status === 'pending'
                                                            ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                                            : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                                }`}
                                            >
                                                {transaction.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <FiDollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 dark:text-gray-400">
                                No transactions yet. Start creating gigs to earn money!
                            </p>
                        </div>
                    )}
                </div>

                {/* Commission Info */}
                {user && (
                    <div className="mt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <CommissionBreakdown
                                grossAmount={earningsData.averageEarningsPerGig}
                                platformCommission={user.commissionSettings?.platformCommission || 0.14}
                                buyerProtectionFee={user.commissionSettings?.buyerProtectionFee || 0.02}
                                currency="GHS"
                                showDetails={true}
                            />

                            {/* Payment Info */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Payment Information
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                            Next Payout
                                        </h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Payments are processed every Friday
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                            Total Gigs Completed
                                        </h4>
                                        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                                            {earningsData.totalCompletedGigs}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                            Subscription Tier
                                        </h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                                            {user.subscription?.tier || 'Free'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default EarningsPage